import { useForm, UseFormReturn } from "react-hook-form";
import MultiStepForm from "@/components/MultiStepForm";
import { DateTime } from "luxon";
import {
  AssistedJobPostDraftSchema,
  AssistedJobPostPublishSchema,
  SelfServeJobPostSchema,
  SelfServeJobPostDraftSchema
} from "@/components/Forms/JobPostForm/schema";
import {
  AdvancedJobPostDraftSchema,
  AdvancedJobPostPublishSchema
} from "@/components/Forms/JobPostForm/advancedSchema";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  HiringStatus,
  JobPostCategory,
  PostStage,
  ServiceLevel,
  isExecuSearchServiceLevel
} from "@/pages/_layouts/jobs/types";
import { JobDetailsStep } from "@/components/Forms/JobPostForm/steps/JobDetailsStep";
import { JobDescriptionStep } from "@/components/Forms/JobPostForm/steps/JobDescriptionStep";
import { EducationAndExperienceStep } from "@/components/Forms/JobPostForm/steps/EducationAndExperienceStep";
import { CompensationStep } from "@/components/Forms/JobPostForm/steps/CompensationStep";
import { ContactInformationStep } from "@/components/Forms/JobPostForm/steps/ContactInformationStep";
import { ApplicationFormStep } from "@/components/Forms/JobPostForm/steps/ApplicationFormStep";
import { JobPostFormInput, FormStep, JobPostFormProps } from "./types.ts";
import TextField from "@/components/TextField/TextField.tsx";
import { useQuery } from "@apollo/client";
import { useSomePeopleQuery } from "@/pages/people/_hooks/useSomePeopleQuery";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useSearchParams } from "react-router-dom";
import { JOB_POST_DETAILS_QUERY } from "@/pages/_layouts/jobs/queries";
import { formatJobPostForForm, isSubmissionDeadlineInPast, toSingleDateSubmissionDeadline } from "./utils.tsx";
import PgaConfetti from "@/components/PgaConfetti";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useFormValidation } from "./hooks/useFormValidation";
import { ActionButton } from "./components";
import { Action } from "@/components/Banner/shared";
import { AddEmployerRoleModal } from "./_AddEmployerRoleModal";
import { useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import {
  JOB_APPLICANT_COUNT_ESTIMATE_FLAG,
  JobApplicantCountEstimateFormSection
} from "@/features/jobs/applicant-count-estimate";

// Helper function to select appropriate schema
const getSchema = (serviceLevel: string, submitAction: string, variant: "draft" | "primary") => {
  const isAssisted = serviceLevel === "Assisted";
  if (variant === "draft") {
    if (serviceLevel === "Self-Serve") {
      return SelfServeJobPostDraftSchema;
    }
    return isAssisted ? AssistedJobPostDraftSchema : AdvancedJobPostDraftSchema;
  }
  if (serviceLevel === "Self-Serve") {
    return SelfServeJobPostSchema;
  }
  // Submit action "Save" in this instance is a primary action to save edits to a job post either already published or in a publishable state.
  if (submitAction === "Save") {
    return isAssisted ? AssistedJobPostPublishSchema : AdvancedJobPostPublishSchema;
  }

  if (isAssisted) {
    return submitAction === "Send for Review" ? AssistedJobPostPublishSchema : AssistedJobPostDraftSchema;
  } else {
    return submitAction === "Save & Publish" ? AdvancedJobPostPublishSchema : AdvancedJobPostDraftSchema;
  }
};

export default function JobPostForm({
  isEdit = false,
  defaultValues,
  submitDialogContent = isEdit
    ? "Are you sure you want to update this job post?"
    : "Are you sure you want to save this job post?",
  onSubmit,
  moreActions,
  formRef,
  isSubmitting = false,
  isSecondarySubmitting = false,
  triggerConfetti = false,
  onConfettiComplete,
  errorBanner,
  setErrorBanner,
  errorFacilityId,
  setErrorFacilityId
}: JobPostFormProps) {
  const [initialEmployerLoad, setInitialEmployerLoad] = useState(true);
  const [isAddEmployerRoleModalOpen, setIsAddEmployerRoleModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get("duplicate");

  // Query to fetch job post details when duplicating
  const { data: duplicateData, loading: duplicateLoading } = useQuery(JOB_POST_DETAILS_QUERY, {
    variables: { jobPostId: duplicateId },
    skip: !duplicateId
  });

  const formDefaultValues = useCallback(
    (duplicateDataFromQuery?: any): JobPostFormInput => {
      const createDefaultValues: JobPostFormInput = {
        name: "",
        overview: "",
        stage: "Draft",
        serviceLevel: "Assisted",
        status: "In Progress",
        facilityId: "",
        facilityName: "",
        compensation: {
          baseMaximum: undefined,
          baseMinimum: undefined,
          totalAnticipated: undefined,
          currencyUnit: "USD" as const
        },
        benefits: "",
        additionalIncome: "",
        pointOfContact: {
          name: "",
          email: "",
          title: "",
          phone: ""
        },
        employerId: "",
        employerData: null,
        category: "",
        requiredEducation: "N/A",
        requiredExperienceInYears: "0",
        requiredExperienceDescription: "",
        essentialResponsibilities: "",
        hideCompensationFromJobBoard: false,
        applicantGroup: "PGA Professional and General Public",
        applyVia: "PGA.org / Email Notification",
        paySchedule: "Salary",
        positionType: "Year Round",
        otherSections: [],
        otherStates: [],
        submissionDeadline: {
          startDate: DateTime.now().plus({ months: 1 }).toFormat("yyyy-MM-dd"),
          endDate: DateTime.now().plus({ months: 1 }).toFormat("yyyy-MM-dd")
        },
        additionalPgaEducation: "N/A",
        externalJobUrl: "",
        sendForReview: false,
        sendForReviewDescription: ""
      };

      const createAdvancedDefaultValues: JobPostFormInput = {
        ...createDefaultValues,
        serviceLevel: "Advanced",
        applicantGroup: "",
        hideCompensationFromJobBoard: true,
        applyVia: "Facility Website",
        submissionDeadline: {
          startDate: undefined,
          endDate: undefined
        }
      };

      // Path 1: Duplicating an existing job (highest precedence)
      if (duplicateDataFromQuery) {
        const { id, ...jobPost } = duplicateDataFromQuery;
        const isAdvancedOrExecutive = jobPost.serviceLevel === "Advanced" || jobPost.serviceLevel === "Executive";
        const baseValues = isAdvancedOrExecutive ? createAdvancedDefaultValues : createDefaultValues;

        const duplicateValues = {
          ...baseValues,
          ...jobPost,
          name: `${jobPost.name}`,
          stage: "Draft",
          status: "In Progress",
          employerId: jobPost.employerId ?? "",
          submissionDeadline: isAdvancedOrExecutive
            ? toSingleDateSubmissionDeadline(jobPost.submissionDeadline)
            : {
                startDate: DateTime.now().plus({ months: 1 }).toFormat("yyyy-MM-dd"),
                endDate: DateTime.now().plus({ months: 1 }).toFormat("yyyy-MM-dd")
              },
          category: jobPost.category?.name || "",
          compensation: jobPost.compensation
            ? {
                baseMaximum: jobPost.compensation?.baseMaximum ? jobPost.compensation.baseMaximum * 100 : undefined,
                baseMinimum: jobPost.compensation?.baseMinimum ? jobPost.compensation.baseMinimum * 100 : undefined,
                totalAnticipated: jobPost.compensation?.totalAnticipated
                  ? jobPost.compensation.totalAnticipated * 100
                  : undefined,
                currencyUnit: jobPost.compensation?.currencyUnit || "USD"
              }
            : baseValues.compensation,
          requiredExperienceInYears: jobPost.requiredExperienceInYears?.toString() || "0",
          pointOfContact: {
            name: jobPost.pointOfContact?.name || "",
            email: jobPost.pointOfContact?.email || "",
            title: jobPost.pointOfContact?.title || "",
            phone: jobPost.pointOfContact?.phone || ""
          }
        };

        if (!isEdit) {
          delete duplicateValues.owners;
        }

        return duplicateValues;
      }

      // Path 2: Editing an existing job OR Creating from an upload
      if (defaultValues) {
        const isAdvancedOrExecutive =
          defaultValues.serviceLevel === "Advanced" || defaultValues.serviceLevel === "Executive";
        const baseValues = isAdvancedOrExecutive ? createAdvancedDefaultValues : createDefaultValues;

        let categoryString = "";
        if (defaultValues.category) {
          if (typeof defaultValues.category === "object" && defaultValues.category !== null) {
            const categoryObj = defaultValues.category as JobPostCategory;
            categoryString = categoryObj.name || "";
          } else {
            categoryString = String(defaultValues.category || "");
          }
        }

        const getSubmissionDeadline = () => {
          if (isAdvancedOrExecutive) {
            if (!defaultValues.submissionDeadline) {
              return { startDate: undefined, endDate: undefined };
            }
            if (
              typeof defaultValues.submissionDeadline === "string" ||
              defaultValues.submissionDeadline instanceof Date
            ) {
              return toSingleDateSubmissionDeadline(defaultValues.submissionDeadline);
            }
            // Assuming it's already in {startDate, endDate} format if not string/Date
            return defaultValues.submissionDeadline;
          }

          // Logic for non-advanced (e.g., Assisted)
          if (!defaultValues.submissionDeadline) {
            return createDefaultValues.submissionDeadline; // Fallback for new assisted from upload
          }

          if (
            typeof defaultValues.submissionDeadline === "string" ||
            defaultValues.submissionDeadline instanceof Date
          ) {
            const normalizedSubmissionDeadline = toSingleDateSubmissionDeadline(defaultValues.submissionDeadline);
            if (normalizedSubmissionDeadline.startDate) {
              return normalizedSubmissionDeadline;
            } else {
              return createDefaultValues.submissionDeadline;
            }
          }
          // Assuming it's already in {startDate, endDate} format and is valid for assisted
          return defaultValues.submissionDeadline;
        };

        const populatedResult = {
          ...baseValues,
          ...defaultValues,
          employerId: defaultValues.employerId ?? "",
          category: categoryString,
          submissionDeadline: getSubmissionDeadline(),
          compensation: defaultValues.compensation
            ? {
                baseMaximum: defaultValues.compensation?.baseMaximum
                  ? defaultValues.compensation.baseMaximum * 100
                  : undefined,
                baseMinimum: defaultValues.compensation?.baseMinimum
                  ? defaultValues.compensation.baseMinimum * 100
                  : undefined,
                totalAnticipated: defaultValues.compensation?.totalAnticipated
                  ? defaultValues.compensation.totalAnticipated * 100
                  : undefined,
                currencyUnit: defaultValues.compensation?.currencyUnit || "USD"
              }
            : baseValues.compensation,
          requiredExperienceInYears: defaultValues.requiredExperienceInYears?.toString() || "0",
          pointOfContact: {
            name: defaultValues.pointOfContact?.name || "",
            email: defaultValues.pointOfContact?.email || "",
            title: defaultValues.pointOfContact?.title || "",
            phone: defaultValues.pointOfContact?.phone || ""
          }
        };

        if (!isEdit) {
          delete populatedResult.owners;
          populatedResult.stage = "Draft";
          populatedResult.status = "In Progress";
          if (!["Assisted", "Advanced", "Executive", "Self-Serve"].includes(populatedResult.serviceLevel || "")) {
            populatedResult.serviceLevel = "Assisted";
            if (isAdvancedOrExecutive) {
              populatedResult.submissionDeadline = createDefaultValues.submissionDeadline;
            }
          }
          if (populatedResult.serviceLevel === "Assisted" && !populatedResult.submissionDeadline?.startDate) {
            populatedResult.submissionDeadline = createDefaultValues.submissionDeadline;
          }
        }

        return populatedResult;
      }

      // Path 3: Fresh create (no duplicate, no 'defaultValues' prop from parent)
      return createDefaultValues;
    },
    [isEdit, defaultValues]
  );

  // Determine initial values for useForm based on whether we are duplicating
  const initialFormValues = duplicateId ? formDefaultValues(duplicateData) : formDefaultValues();

  const form = useForm<JobPostFormInput>({
    mode: "onChange",
    defaultValues: initialFormValues
  });

  const [requiresReview, setRequiresReview] = useState(false);
  const [isDraft, setIsDraft] = useState(!isEdit);
  const [jobPostReviewRequestNotes, setJobPostReviewRequestNotes] = useState("");
  const [submitAction, setSubmitAction] = useState("Save & Publish");
  const [secondaryAction, setSecondaryAction] = useState("Save");
  const [isSecondaryActionSubmitting, setIsSecondaryActionSubmitting] = useState(false);

  const [internalRunConfettiActive, setInternalRunConfettiActive] = useState(false);
  const { isCompactLayout: isMobileDisplay } = useIsCompactLayout();

  useEffect(() => {
    if (triggerConfetti) {
      setInternalRunConfettiActive(true);
    }
  }, [triggerConfetti]);

  const handleInternalConfettiComplete = () => {
    setInternalRunConfettiActive(false);
    if (onConfettiComplete) {
      onConfettiComplete();
    }
  };

  const storedEmployerData = form.watch("employerData");
  const employerName =
    storedEmployerData?.name || `${storedEmployerData?.firstName || ""} ${storedEmployerData?.lastName || ""}`.trim();

  // Query to fetch employer data if needed
  const { data: employerData } = useSomePeopleQuery({
    variables: {
      input: { search: employerName || "" },
      first: 10
    },
    skip: !employerName || employerName.length < 2 // Only skip if no search term or too short
  });

  const handleFormValidation = async () => {
    const formData = form.getValues();

    const schema = getSchema(formData.serviceLevel, submitAction, "primary");
    const validationResult = await schema.safeParseAsync(formData);
    return validationResult?.success;
  };

  const handleFormSubmit = async (data: JobPostFormInput) => {
    const schema = getSchema(data.serviceLevel, submitAction, "primary");
    const validationResult = await schema.safeParseAsync(data);

    if (!validationResult.success) {
      setErrorBanner?.("Please fix form errors before submitting");
      console.error(validationResult.error.issues);
      return;
    }

    setErrorBanner?.(undefined);

    // Exclude status; not sent to backend here.
    const { status, ...jobPostData } = data;

    const cleanedData = {
      ...jobPostData,
      employerId: jobPostData.employerId || "",
      ...(submitAction === "Save & Publish"
        ? { stage: PostStage.ACTIVE }
        : requiresReview
          ? { stage: PostStage.IN_REVIEW }
          : {}),
      isValid: validationResult.success,
      isDraft: submitAction === "Save & Publish" ? false : isDraft,
      sendForReview: requiresReview,
      sendForReviewDescription: jobPostReviewRequestNotes
    };

    try {
      await onSubmit(cleanedData, isSecondaryActionSubmitting);
    } catch (error: any) {
      // Let the error propagate up to be handled by MultiStepForm
      throw error;
    }
  };

  const handleSecondaryAction = async () => {
    setIsSecondaryActionSubmitting(true);

    const data = form.getValues();
    const schema = getSchema(data.serviceLevel, submitAction, "draft");

    const validationResult = await schema.safeParseAsync(data);

    const cleanedData = {
      ...data,
      employerId: data.employerId || "",
      isDraft: isDraft,
      isValid: validationResult.success
    };

    if (!validationResult.success) {
      setErrorBanner?.("Please fix form errors before saving draft");
      setIsSecondaryActionSubmitting(false);
      return;
    }

    setErrorBanner?.(undefined);

    try {
      await onSubmit(cleanedData, true);
    } catch (error) {
      // Let the error propagate up to be handled by MultiStepForm
      throw error;
    } finally {
      setIsSecondaryActionSubmitting(false);
    }
  };

  const serviceLevel = form.watch("serviceLevel");
  const canManageJobPosts = useCanManageJobPostsBool();
  const flags = useFlags();
  const showApplicantCountEstimate = Boolean(canManageJobPosts && flags[JOB_APPLICANT_COUNT_ESTIMATE_FLAG]);
  const isAssisted = serviceLevel === "Assisted";
  const isSelfServe = serviceLevel === "Self-Serve";
  const isExecuSearch = isExecuSearchServiceLevel(serviceLevel);
  const isApplyViaFacilityWebsite = form.watch("applyVia") === "Facility Website";

  // Use the custom validation hook
  const validation = useFormValidation(form, getSchema, serviceLevel, submitAction);

  const steps: FormStep[] = useMemo(
    () => [
      {
        headline: "Job Details",
        supportingText: "Step 1",
        requiredFields: [
          "category",
          "name",
          "facilityId",
          "serviceLevel",
          "sectionId",
          "positionType",
          ...(isAssisted ? ["applicantGroup"] : []),
          ...(isAssisted ? ["submissionDeadline"] : []),
          ...(isApplyViaFacilityWebsite ? ["externalJobUrl"] : [])
        ],
        renderContent: (form: UseFormReturn<JobPostFormInput>) => <JobDetailsStep form={form} isEdit={isEdit} />
      },
      {
        headline: "Job Description",
        supportingText: "Step 2",
        requiredFields: ["overview"],
        renderContent: (form: UseFormReturn<JobPostFormInput>) => <JobDescriptionStep form={form} />
      },
      ...(isAssisted || isSelfServe
        ? [
            {
              headline: "Education and Experience",
              supportingText: "Step 3",
              requiredFields: ["requiredEducation", "requiredExperienceInYears", "requiredExperienceDescription"],
              renderContent: (form: UseFormReturn<JobPostFormInput>) => <EducationAndExperienceStep form={form} />
            },
            {
              headline: "Compensation",
              supportingText: "Step 4",
              requiredFields: ["compensation.baseMinimum", "compensation.baseMaximum", "compensation.totalAnticipated"],
              renderContent: (form: UseFormReturn<JobPostFormInput>) => <CompensationStep form={form} isEdit={isEdit} />
            }
          ]
        : [
            {
              headline: "Compensation and Education",
              supportingText: "Step 3",
              requiredFields: ["requiredEducation", "requiredExperienceInYears"],
              renderContent: (form: UseFormReturn<JobPostFormInput>) => <CompensationStep form={form} isEdit={isEdit} />
            },
            ...(isExecuSearch
              ? [
                  {
                    headline: "Application Form",
                    supportingText: "Step 4",
                    requiredFields: [],
                    renderContent: (form: UseFormReturn<JobPostFormInput>) => <ApplicationFormStep form={form} />
                  }
                ]
              : [])
          ]),

      {
        headline: "Contact Information",
        supportingText: isAssisted || isSelfServe || isExecuSearch ? "Step 5" : "Step 4",
        requiredFields: [
          ...(!isSelfServe && !isExecuSearch ? ["employerId"] : []),
          "pointOfContact.name",
          "pointOfContact.email"
        ],
        renderContent: (form: UseFormReturn<JobPostFormInput>) => <ContactInformationStep form={form} isEdit={isEdit} />
      }
    ],
    [isAssisted, isSelfServe, isExecuSearch, isEdit, isApplyViaFacilityWebsite]
  );

  const processedSteps = useMemo(
    () =>
      steps.map((step) => ({
        ...step,
        content: step.renderContent(form),
        requiredFields: step.requiredFields
      })),
    [steps, form]
  );

  // Update state variables from validation hook
  const isDraftValid = validation.isDraftValid;
  const isPublishValid = validation.isPublishValid;

  // Establish submission deadline for conditional logic
  const submissionDeadline = form.watch("submissionDeadline");
  const deadlineInPast = isSubmissionDeadlineInPast(submissionDeadline?.endDate);
  const shouldAllowUnposted = !isEdit && deadlineInPast;

  useEffect(() => {
    handleFormValidation();

    const values = form.getValues();

    if (!isEdit) {
      setIsDraft(true);
    }

    switch (values.stage) {
      case PostStage.DRAFT:
      case PostStage.PENDING:
      case PostStage.ASSIST:
        if (values.serviceLevel === ServiceLevel.ASSISTED && !shouldAllowUnposted) {
          setRequiresReview(true);
          setSubmitAction("Send for Review");
          return;
        } else {
          setRequiresReview(false);
          setSubmitAction("Save & Publish");
          return;
        }
      case PostStage.IN_REVIEW:
        setSubmitAction("Save");
        setSecondaryAction("");
        return;
      case PostStage.INACTIVE:
        if (values.status === HiringStatus.FILLED || values.status === HiringStatus.INCOMPLETE) {
          setSubmitAction("Save");
          setSecondaryAction("");
        }
        return;
      case PostStage.ACTIVE:
        setSubmitAction("Save");
        setSecondaryAction("");
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.watch(), handleFormValidation, isEdit]);

  // Handle initial employer data loading
  useEffect(() => {
    if (!storedEmployerData) return;

    // If we already have an ID, just use it
    if (storedEmployerData.id) {
      form.setValue("employerId", storedEmployerData.id);
      setInitialEmployerLoad(false);
      return;
    }

    // If we have employer data from the query, find and set the matching employer
    if (employerData?.people?.nodes && employerName) {
      // First try to find by email if available
      const email = storedEmployerData.email;
      if (email) {
        const emailMatch = employerData.people.nodes.find(
          (person: any) => person.email?.toLowerCase() === email.toLowerCase()
        );
        if (emailMatch) {
          form.setValue("employerData", emailMatch);
          form.setValue("employerId", emailMatch.id);
          setInitialEmployerLoad(false);
          return;
        }
      }

      // Fall back to name search if no email match
      const nameMatch = employerData.people.nodes.find((person: any) => {
        const personName = person.name || `${person.firstName || ""} ${person.lastName || ""}`.trim();
        return personName.toLowerCase() === employerName.toLowerCase();
      });

      if (nameMatch) {
        form.setValue("employerData", nameMatch);
        form.setValue("employerId", nameMatch.id);
      } else if (initialEmployerLoad) {
        // Only clear data during initial load
        form.setValue("employerData", null);
        form.setValue("employerId", "");
      }
    }

    setInitialEmployerLoad(false);
  }, [storedEmployerData, employerData, employerName, initialEmployerLoad, form]);

  // Expose form methods to parent component if formRef is provided
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        getValues: () => form.getValues()
      };
    }
  }, [formRef, form]);

  // Sync form with defaultValues prop changes. For edit mode, only sync once to prevent
  // losing user's form edits during re-renders.
  const { reset } = form;
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (duplicateId && duplicateData) {
      reset(formDefaultValues(formatJobPostForForm(duplicateData.jobPost)));
      hasInitializedRef.current = true;
      return;
    }

    if (isEdit && hasInitializedRef.current) {
      return;
    }

    if (!duplicateId && defaultValues) {
      reset(formDefaultValues());
      hasInitializedRef.current = true;
    }
  }, [defaultValues, duplicateId, duplicateData, reset, formDefaultValues, isEdit]);

  const segments = window.location.pathname.split("/");

  let newUrl = "";
  if (isEdit) {
    const jobPostId = segments[segments.length - 1];
    const basePath = segments.slice(0, -2).join("/"); // Remove "edit/jobPostId"
    newUrl = `${basePath}/${jobPostId}/details`;
  } else {
    newUrl = `${segments.slice(0, -2).join("/")}`;
  }

  const backLink = {
    to: newUrl,
    title: "Back"
  };

  const unpostedJobWarning = (
    <div className="overflow-auto -mt-2 space-y-6">
      <div>
        This job post has a submission deadline in the past. This will cause the job post to be created in the{" "}
        <strong>Unposted</strong> stage. It will not appear on the job board.
      </div>
    </div>
  );

  const sendJobPostReview = (
    <div className="overflow-auto -mt-2 space-y-6">
      <div>
        Sending a job post for review sends an email to the Employer that includes a link to this job post on the job
        board. You may also include an optional note for context. The Employer will need to review the job, accept terms
        and conditions, and publish the job to make it visible to potential applicants.
      </div>

      <TextField
        onChangeText={(text) => {
          setJobPostReviewRequestNotes(text);
        }}
        value={jobPostReviewRequestNotes}
        label="Recipient Note"
        type="textarea"
        rows={6}
        className="w-full"
      />
    </div>
  );

  // Confetti configuration parameters
  const numberOfPieces = isMobileDisplay ? 50 : 130;
  const xVelocityModifier = isMobileDisplay ? 100 : 200;
  const yVelocityModifier = isMobileDisplay ? 60 : 120;

  // Calculate tooltip states using validation hook data
  const showPublishTooltip =
    validation.publishMissingFields.length > 0 && !isPublishValid && !isSecondaryActionSubmitting && !isSubmitting;
  const showDraftTooltip = !isDraftValid && !isSecondaryActionSubmitting && !isSubmitting;

  // Create tooltip content for publish button if needed
  const publishTooltipContent = showPublishTooltip ? (
    <ul className="list-disc pl-4 space-y-1">
      {validation.publishMissingFields.map((field, index) => (
        <li key={index}>
          <span className="text-sm">{field.label}</span>
        </li>
      ))}
    </ul>
  ) : undefined;

  if (duplicateLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg font-medium">Loading job post data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {internalRunConfettiActive && (
        <PgaConfetti
          run={internalRunConfettiActive}
          recycle={false}
          numberOfPieces={numberOfPieces}
          wind={0}
          gravity={0.7}
          initialVelocityX={Math.random() * xVelocityModifier + Math.random() * yVelocityModifier}
          initialVelocityY={Math.random() * yVelocityModifier - Math.random() * xVelocityModifier}
          tweenDuration={0.4}
          variant="golf"
          onConfettiComplete={handleInternalConfettiComplete}
        />
      )}
      <MultiStepForm
        form={form}
        steps={processedSteps}
        leadingContent={showApplicantCountEstimate ? <JobApplicantCountEstimateFormSection form={form} /> : undefined}
        renderStepper={true}
        onSubmit={handleFormSubmit}
        backLink={backLink}
        validationSchema={getSchema(form.watch("serviceLevel"), submitAction, "primary")}
        modelName="Job"
        customHeadline={requiresReview ? "Send for Review" : null}
        isEditing={isEdit}
        enableSubmitOnLastStep={false}
        showActions={true}
        disabledSubmit={!isPublishValid || isSecondaryActionSubmitting || isSubmitting}
        submitText={submitAction}
        submitDialogContent={
          requiresReview ? sendJobPostReview : shouldAllowUnposted ? unpostedJobWarning : submitDialogContent
        }
        isSubmitting={isSubmitting}
        errorBanner={errorBanner}
        setErrorBanner={(errorBanner) => setErrorBanner?.(errorBanner || undefined)}
        errorPrimaryAction={
          errorFacilityId &&
          errorBanner?.includes("Employer does not have Manage Job role at assigned facility") &&
          storedEmployerData
            ? ({
                label: "Add Employer Role",
                onClick: () => setIsAddEmployerRoleModalOpen(true)
              } as Action)
            : undefined
        }
        submitButtonTooltip={publishTooltipContent}
        moreActions={[
          ...(moreActions || []),
          !isEdit && (
            <ActionButton
              key="saveDraft"
              onClick={handleSecondaryAction}
              disabled={!isDraftValid || isSecondaryActionSubmitting || isSubmitting}
              isLoading={isSecondaryActionSubmitting}
              variant="outlined"
              missingFields={validation.draftMissingFields}
              showTooltip={showDraftTooltip}
            >
              {secondaryAction}
            </ActionButton>
          ),
          isEdit && secondaryAction && (
            <ActionButton
              key="secondaryAction"
              onClick={handleSecondaryAction}
              disabled={!isDraftValid || isSecondarySubmitting || isSubmitting}
              isLoading={isSecondarySubmitting}
              variant="outlined"
              missingFields={validation.draftMissingFields}
              showTooltip={showDraftTooltip}
            >
              {secondaryAction}
            </ActionButton>
          )
        ].filter(Boolean)}
      />
      {errorFacilityId && storedEmployerData && (
        <AddEmployerRoleModal
          open={isAddEmployerRoleModalOpen}
          onClose={() => setIsAddEmployerRoleModalOpen(false)}
          employer={{
            id: storedEmployerData.id,
            firstName: storedEmployerData.firstName || "",
            lastName: storedEmployerData.lastName || "",
            displayName:
              storedEmployerData.displayName ||
              storedEmployerData.name ||
              `${storedEmployerData.firstName || ""} ${storedEmployerData.lastName || ""}`.trim(),
            profilePhoto: storedEmployerData.profilePhoto,
            email: storedEmployerData.email,
            primaryFacility: storedEmployerData.primaryFacility
          }}
          facilityId={errorFacilityId}
          onSuccess={() => {
            setErrorBanner?.(undefined);
            setErrorFacilityId?.(null);
            showSnackbar("Role added", { subtitle: "You can now try saving the job post again." });
          }}
        />
      )}
    </>
  );
}
